import React from 'react'
import { Navigate } from 'react-router-dom'

import { useSupportListRoute } from 'hooks/api'

/**
 * What `/support/requests` is now (VRG-324).
 *
 * ## Why a redirect and not a deletion
 *
 * That URL is in bookmarks, in `What's new` announcement bodies ("you asked for
 * this"), and in every request thread that ever said "on the company board". The
 * page behind it is gone — the customer feature board was a surface you had to
 * VISIT in order to rank things you had already asked for, which is why nobody
 * did — but 404-ing the people who followed a link we ourselves wrote is the worst
 * available ending for them. They came looking for the status of an idea; Company
 * → Support has exactly that, now with the board status on the row.
 *
 * ## Why it is a component and not a `<Navigate>` in the route table
 *
 * The destination DEPENDS ON THE CALLER, and the rule lives in
 * `useSupportListRoute` because two surfaces need it (here, and the What's-new
 * "you asked for this" links). `Company → Support` needs `View company requests`;
 * a member without it would land on a section their own nav row renders disabled,
 * behind an API that 403s the list — a redirect that delivers you to a permission
 * error is not better than the 404 it replaced. Those members go to `My requests`,
 * which every user can see and which holds the thing they were most likely after:
 * the request THEY filed.
 *
 * ## Why it renders nothing while permissions load
 *
 * The permissions query resolves in a few ms from a 5-minute-stale cache, and this
 * is a pass-through the user should never perceive. A skeleton here would be
 * chrome for a page that is not going to exist either way (§19 governs surfaces
 * that RENDER content; this one renders none). Defaulting to the company route
 * while loading would be worse than a blank frame — it would send unpermissioned
 * users to the 403 above and then correct itself, leaving a dead history entry.
 */
export const SupportBoardRedirect = () => {
  const destination = useSupportListRoute()

  if (!destination) return null

  return (
    // `replace` so Back does not bounce off this redirect and immediately forward
    // again — the classic redirect history trap.
    <Navigate to={destination} replace />
  )
}
